"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
export default function Expertise() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: storyRef,
    isVisible: storyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const paragraphs = [{
    id: 1,
    text: "In the complex landscape of modern hospitality, HotelRevBaBa was founded on a singular conviction — that revenue management should be a science, not a guessing game. What began as a focused consultancy for independent properties has evolved into a rigorous methodology, helping hotel owners navigate the volatile digital marketplace with precision and data-backed confidence.",
    dropcap: true
  }, {
    id: 2,
    text: "Every strategy we deploy is the result of deep analytical rigor. We examine OTA performance, tech stack integration, and market demand signals with the same intensity. We do not believe in generic solutions; we believe in bespoke revenue architectures that align with your property's unique operational goals and long-term asset value.",
    dropcap: false
  }, {
    id: 3,
    text: "Our revenue audits, drawn from years of hands-on experience in high-demand markets, operate with total transparency. Precision, we believe, is the only metric that cannot be compromised — and the only one that drives sustainable profitability.",
    dropcap: false,
    isPullQuote: true
  }, {
    id: 4,
    text: "HotelRevBaBa is not built for mass-market volume. It is built for owners who understand that revenue optimization is a record of the systems and strategies that drive it. We invite you to leverage that record — and to find, within it, a commitment to excellence that will define your property's success for years to come.",
    dropcap: false
  }];
  return <section data-section-id="3004" id="the-expert-behind-the-strategy" className="bg-background text-foreground">
      <div ref={heroRef} className={`relative w-full h-[60vh] min-h-[480px] overflow-hidden transition-opacity duration-1000 ${heroVisible ? "opacity-100" : "opacity-0"}`}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_analytics_dashboard.webp" alt="Hotel revenue analytics dashboard on professional screen" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to bottom, hsla(222, 47%, 11%, 0.2) 0%, hsla(222, 47%, 11%, 0.1) 40%, hsla(222, 47%, 11%, 0.85) 100%)"
      }} />

        <div className="absolute inset-x-0 bottom-0 pb-12 sm:pb-16 md:pb-20 px-6 sm:px-10">
          <div className="max-w-6xl mx-auto text-center">
            <p className={`font-mono text-[10px] sm:text-xs tracking-[0.35em] uppercase text-primary mb-6 transition-all duration-1000 delay-200 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              Our Expertise <span className="mx-2 opacity-60">—</span> Est. 2020
            </p>
            <h1 className={`font-serif font-light text-foreground leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px] transition-all duration-1000 delay-300 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Driven by
              <br />
              <span className="italic font-normal text-primary">Precision</span>
            </h1>
          </div>
        </div>
      </div>

      <div ref={storyRef} className="px-6 sm:px-10 lg:px-16 pt-24 md:pt-36 lg:pt-48 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-8">
                <div className={`transition-all duration-700 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                  <p className="font-serif text-primary text-3xl md:text-4xl font-light mb-3">
                    01 <span className="opacity-60">—</span>
                  </p>
                  <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground">
                    The Methodology
                  </p>
                </div>

                <Separator className="bg-border/60 max-w-[120px]" />

                <div className={`transition-all duration-700 delay-150 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                  <div className="relative w-full max-w-[260px] aspect-[3/4] overflow-hidden mb-6">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_consultant_portrait.png" alt="Sunil Singh, Revenue Management Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                    filter: "sepia(0.35) saturate(0.9) brightness(0.95) contrast(1.05)"
                  }} />
                    <div className="absolute inset-0 pointer-events-none" style={{
                    background: "linear-gradient(180deg, hsla(43, 72%, 52%, 0.08) 0%, hsla(222, 47%, 11%, 0.15) 100%)"
                  }} />
                  </div>
                  <p className="font-serif text-xl md:text-2xl text-foreground leading-snug">
                    Sunil Singh
                  </p>
                  <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mt-2">
                    Lead Consultant &middot; Revenue Expert
                  </p>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8">
              <article className="space-y-10">
                {paragraphs.map((para, i) => {
                if (para.isPullQuote) {
                  return <div key={para.id} data-index={i} className={`relative py-6 md:py-10 px-6 md:px-10 transition-all duration-700 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                        <span aria-hidden="true" className="absolute left-0 top-4 bottom-4 w-px bg-primary" />
                        <span aria-hidden="true" className="absolute right-0 top-4 bottom-4 w-px bg-primary" />
                        <p className="font-serif italic text-primary text-2xl md:text-3xl lg:text-[32px] leading-[1.5] text-center">
                          {para.text}
                        </p>
                      </div>;
                }
                if (para.dropcap) {
                  return <p key={para.id} data-index={i} className={`font-serif text-foreground/90 text-lg leading-[1.8] transition-all duration-700 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                        <span className="float-left font-serif text-primary text-[120px] leading-[0.85] mr-3 mt-1 font-light">
                          I
                        </span>
                        {para.text}
                      </p>;
                }
                return <div key={para.id} data-index={i}>
                      <p className={`font-serif text-foreground/90 text-lg leading-[1.8] transition-all duration-700 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                        {para.text}
                      </p>

                      {i < paragraphs.length - 1 && <div aria-hidden="true" className="flex items-center justify-center gap-4 my-12">
                          <span className="h-px w-12 bg-border" />
                          <svg width="40" height="20" viewBox="0 0 40 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary">
                            <path d="M20 2C16 6 12 10 4 10C12 10 16 14 20 18C24 14 28 10 36 10C28 10 24 6 20 2Z" stroke="currentColor" strokeWidth="0.75" fill="none" />
                            <circle cx="20" cy="10" r="1" fill="currentColor" />
                          </svg>
                          <span className="h-px w-12 bg-border" />
                        </div>}
                    </div>;
              })}

                <div className={`pt-8 transition-all duration-700 delay-200 ${storyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                  <p className="text-primary text-4xl md:text-5xl leading-none" style={{
                  fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                  transform: "rotate(-3deg)",
                  display: "inline-block"
                }}>
                    Sunil Singh
                  </p>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-4">
                    Revenue Management Consultant
                  </p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>;
}